import { Box, Button, Modal, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { FiUpload } from "react-icons/fi";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { DownloadInterface } from "../../common/interfaces";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import PageToaster from "../toaster/PageToaster";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  download?: DownloadInterface | null;
}

const DownloadCreateModal: React.FC<Props> = ({ open, onClose, onSaved, download }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateMode = Boolean(download);
  const containerId = "toaster-download-modal";
  const { autoSuccess, autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  useEffect(() => {
    if (download) {
      setName(download.name);
      setDescription(download.description ?? "");
    } else {
      setName("");
      setDescription("");
    }
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [download, open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      autoError("Informe o nome do download.");
      return;
    }
    if (!updateMode && !file) {
      autoError("Selecione um arquivo.");
      return;
    }

    const formData = new FormData();
    formData.append("name", name.trim());
    if (description.trim()) formData.append("description", description.trim());
    if (file) formData.append("file", file);

    setSaving(true);
    try {
      if (updateMode && download) {
        await api.patch(`/downloads/${download.download_id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        autoSuccess("Download atualizado com sucesso!");
      } else {
        await api.post("/downloads", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        autoSuccess("Download criado com sucesso!");
      }
      onSaved();
      onClose();
    } catch (error) {
      handleHttpError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            width: 420
          }}
        >
          <h2>{updateMode ? "Editar download" : "Novo download"}</h2>

          <TextField
            fullWidth
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            minRows={2}
          />

          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />

          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FiUpload />}
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? file.name : updateMode ? "Substituir arquivo (opcional)" : "Selecionar arquivo"}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button onClick={onClose} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? "Enviando..." : updateMode ? "Atualizar" : "Criar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default DownloadCreateModal;
